/**
 * shared/lib/bespoke-seed/hash.ts — bespoke memory-seeding #1221, Task F4
 * reviewer fix.
 *
 * SINGLE source of truth for the sensory-seed answer hash (Immutable Security
 * Rule 1): SHAKE256(salt_32bytes || answer_utf8, outputLen = 512 bits).
 *
 * Lives in `shared/` — not `client/` or `server/` — for the SAME reason
 * `canonicalize.ts` in this directory does: bank-time (client) and recompute
 * -time (server, ai-origin-guard.ts) MUST be byte-identical, and
 * `server/logic/*` MUST NOT import `client/*` (DEVELOPMENT/code-style.md's
 * dependency-direction table) while `shared/*` may be imported by both. This
 * replaces two previously-separate byte-identical copies
 * (client/src/lib/hash.ts `hashSeedAnswer` + server/logic/seed-interview/
 * ai-origin-guard.ts `recomputeSeedAnswerHash`) with one implementation.
 *
 * `@noble/hashes` is already an allowlisted external on both sides of this
 * import boundary — shared/crypto/envelope.ts imports `@noble/hashes/hkdf`,
 * a `/sha2` submodule import, `/utils`; server/logic/payment-rail/invites.ts
 * uses the package server-side. This file adds a `/sha3` submodule import
 * from the same already-allowlisted package — no new dependency edge
 * (Rule 18).
 *
 * DO NOT change the formula, encoding, salt handling, or output length —
 * this is an L0-protected crypto surface (CLAUDE.md Hash Algorithms by
 * Context; Immutable Security Rule 1).
 *
 * atomicWipe (F-review fix, production F-005, Rule 3): the two plaintext-
 * bearing Uint8Arrays (`input`, `answerBytes`) are wiped immediately after
 * the hash is computed, before return — the house `shared/crypto/atomic-wipe.ts`
 * helper (overwrite-with-random-then-zero), same semantics as
 * server/crypto.ts / client/src/lib/prf-key-derivation.ts. The FORMULA is
 * untouched — this only clears the transient buffers afterward.
 */
import { shake256 } from "@noble/hashes/sha3";
import { atomicWipe } from "../../crypto/atomic-wipe";

/**
 * Hash a sensory seed answer using SHAKE256.
 *
 * Formula: SHAKE256(salt_32bytes || answer_utf8, outputLen = 512 bits)
 *
 * @param answer    The raw sensory answer text (UTF-8, user input)
 * @param saltHex   32-byte hex salt (from vaultSeed.answerSalt)
 *
 * @returns  128-character hex string (512 bits / 64 bytes)
 *
 * IMPORTANT: Do NOT use SHA-256 for this — SHAKE256 is mandated by Rule 1.
 */
export function hashSeedAnswer(answer: string, saltHex: string): string {
  const salt        = hexToBytes(saltHex);
  const answerBytes = new TextEncoder().encode(answer.trim());

  // Concatenate: salt (32 bytes) || answer (variable)
  const input = new Uint8Array(salt.length + answerBytes.length);
  input.set(salt);
  input.set(answerBytes, salt.length);

  // SHAKE256 with 512-bit output (64 bytes = 128 hex chars)
  const hash = shake256(input, { dkLen: 64 });
  const result = bytesToHex(hash);

  // Rule 3 (F-review fix, production F-005): wipe the two transient
  // plaintext-bearing buffers immediately after the hash is computed — the
  // formula/encoding/output above is UNCHANGED, this only clears buffers
  // that are no longer needed.
  atomicWipe(input);
  atomicWipe(answerBytes);

  return result;
}

// ---------------------------------------------------------------------------
// Hex helpers — used by hashSeedAnswer and its callers on both tiers.
// ---------------------------------------------------------------------------

export function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.slice(i, i + 2), 16);
  }
  return bytes;
}

export function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, "0"))
    .join("");
}
